"use client";

import { useFormField } from "uiShadcn/form";
import * as Sentry from "@sentry/nextjs";
import { useFormStatusContext } from "forms/contexts/useFormStatusContext";
import { useFormErrors } from "forms/hooks/useFormErrors";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";
import type { UploadProgressUiState } from "ui/UploadProgressBar/UploadProgressBar";
import { getFileExtensionFromName } from "utils/helpers/getFileExtensionFromName";
import { sanitizeFileName } from "utils/helpers/sanitizeFileName";
import { uploadFileClient } from "utils/uploadFileClient";
export function supportsUploadProgress(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }
  const userAgent = navigator.userAgent;
  const isEdge = /Edg/.test(userAgent);
  const isChrome = /chrome/i.test(userAgent) && !isEdge;
  return isEdge || isChrome;
}
interface UseFileProps {
  onChange: (file: {
    id: string;
  } | null) => void;
  validators?: Record<string, unknown>[];
}
export default function useFile({
  onChange,
  validators
}: UseFileProps) {
  const {
    formatMessage
  } = useIntl();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgressUiState | null>(null);
  const {
    error,
    name
  } = useFormField();
  const {
    setError,
    clearErrors
  } = useFormContext();
  const {
    setLoading
  } = useFormStatusContext();
  const {
    handleFileUploadError
  } = useFormErrors();
  const extensions = validators?.find(validator => validator?.validatorType === "filenameExtensionValidator");
  const extensionsArray = extensions?.oneOf as string[] ?? [];
  const allowedExtensions = extensionsArray.join(", ");
  const maxSizeValidator = validators?.find(validator_0 => validator_0?.validatorType === "fileMaxSizeValidator");
  const maxSize = Number(maxSizeValidator?.size ?? 0);
  const maxSizeForMp4 = Number(maxSizeValidator?.sizeForMp4 ?? 0);
  const maxSizeInMB = Number(maxSize) / 1024 / 1024;
  const mp4MaxSizeBytes = Math.max(0, maxSizeForMp4);
  const maxSizeInMBForMp4 = Number(maxSizeForMp4) / 1024 ** 2;
  const onChangeHandler = async (file: File | null) => {
    if (error) {
      clearErrors(name);
    }
    if (!file) {
      onChange(null);
      setUploadProgress(null);
      return;
    }
    const fileExt = getFileExtensionFromName(file?.name);
    const isMp4 = fileExt === ".mp4";
    if (isMp4 && mp4MaxSizeBytes > 0 && file.size > mp4MaxSizeBytes) {
      setUploadProgress(null);
      setError(name, {
        type: "invalidFileSize",
        message: formatMessage({
          id: "fileSizeMp4"
        }, {
          size: maxSizeInMBForMp4
        })
      });
      return;
    }
    if (maxSize && file.size > maxSize && (!isMp4 || mp4MaxSizeBytes <= 0)) {
      setUploadProgress(null);
      setError(name, {
        type: "invalidFileSize",
        message: formatMessage({
          id: "fileSize"
        }, {
          size: maxSizeInMB
        })
      });
      return;
    }
    if (!extensionsArray.includes(fileExt)) {
      setUploadProgress(null);
      setError(name, {
        type: "invalidFileExtension",
        message: formatMessage({
          id: "forbiddenExtension"
        })
      });
      return;
    }
    setIsUploading(true);
    setLoading(true);
    const canTrackUploadProgress = supportsUploadProgress();
    setUploadProgress(canTrackUploadProgress ? {
      percent: 0
    } : null);
    try {
      const sendFileResult = await uploadFileClient(sanitizeFileName(file), canTrackUploadProgress ? {
        onUploadProgress: progress => {
          const percent = progress.totalBytes === 0 ? 0 : Math.min(100, Math.round(progress.percent * 100));
          setUploadProgress({
            percent
          });
        }
      } : undefined);
      if (sendFileResult?.isSuccess && sendFileResult?.data?.fileid) {
        onChange({
          id: sendFileResult.data.fileid
        });
        setUploadProgress(null);
        return;
      }
      if (!sendFileResult.isSuccess && sendFileResult.error) {
        onChange(null);
        handleFileUploadError({
          error: sendFileResult.error,
          setError,
          name,
          maxSizeInMB,
          maxSizeInMBForMp4: isMp4 ? maxSizeInMBForMp4.toString() : undefined
        });
        setUploadProgress(null);
        return;
      }
      setUploadProgress(null);
    } catch (err) {
      Sentry.withScope(scope => {
        scope.setTag("feature", "send-file");
        scope.setContext("File", {
          maxSize,
          fileSize: file?.size,
          fileType: file?.type,
          fileName: file?.name
        });
        Sentry.captureException(err);
      });
      setError(name, {
        type: "sendFileError",
        message: formatMessage({
          id: "fileUploadError"
        })
      });
      setUploadProgress(null);
    } finally {
      setIsUploading(false);
      setLoading(false);
    }
  };
  return {
    allowedExtensions,
    maxSizeInMB,
    maxSizeInMBForMp4,
    onChangeHandler,
    isUploading,
    uploadProgress
  };
}